import { useEffect, useState } from "react"
import { useFormik } from "formik"
import Loader from "../../components/Loader"
import PatientOrdersStatistics from "./components/PatientOrdersStatistics"
import OrderResultDetails from "./components/OrderResultDetails"
import useFetchPatientIssuers from "../../hooks/useFetchPatientIssuers"
import DashboardStatisticsCards from "./components/DashboardStatisticsCards"
import { DateTime } from "luxon"
import { dbDateFormat } from "../../constants"
import DatePicker from "../../components/DatePicker"
import { orderStatisticsFilterSchema } from "./schema"
import { useSearchParams } from "react-router-dom"

export const OrderComponents = () => {
  const { data, isLoading } = useFetchPatientIssuers()
  // Define sorting states for each component
  const [orderStatsSorting, _] = useState([
    { id: "PatientIssuer", desc: false },
  ])
  const [resultStatsSorting, setResultStatsSorting] = useState([
    { id: "PatientIssuer", desc: false },
  ])
  const [searchParams, setSearchParams] = useSearchParams()

  const fromDate = searchParams.get("from") ?? null
  const toDate = searchParams.get("to") ?? null

  useEffect(() => {
    if (!fromDate || !toDate) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.set("from", DateTime.now().toFormat(dbDateFormat))
        newParams.set("to", DateTime.now().toFormat(dbDateFormat))
        return newParams
      })
    }
  }, [fromDate, setSearchParams, toDate])

  // Central formik to manage all filters
  const formik = useFormik({
    initialValues: {
      from: fromDate || DateTime.now().toFormat(dbDateFormat),
      to: toDate || DateTime.now().toFormat(dbDateFormat),
      patientIssuer: "",
      status: "",
    },
    validationSchema: orderStatisticsFilterSchema,
    onSubmit: () => {},
    validateOnChange: true,
  })

  const { values, setFieldValue, errors } = formik

  if (isLoading) {
    return (
      <div className="py-12 flex justify-center items-center gap-3">
        <Loader className="border-primary" />
        <p>Loading...</p>
      </div>
    )
  }

  return (
    <div className="mt-2 py-2 px-4 flex flex-col gap-3">
      <div className="bg-primary p-6 rounded-lg shadow-md mb-6">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <h1 className="text-2xl text-white font-bold mb-4 md:mb-0">
            Statistics Dashboard
          </h1>

          <div className="flex flex-wrap items-center gap-4">
            {/* Patient Issuer filter */}
            <div className="flex flex-col">
              <label className="text-xs mb-1 text-white/80">
                Patient Issuer
              </label>
              <select
                className="pl-2 py-[2px] border rounded text-sm"
                value={values.patientIssuer}
                onChange={(e) => setFieldValue("patientIssuer", e.target.value)}
              >
                <option value="">All</option>
                {data?.orders?.map((item, index) => (
                  <option key={index} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            {/* Date range filters */}
            <div className="flex gap-4 items-end">
              <div className="flex flex-col">
                <label className="text-xs mb-1 text-white/80">From Date</label>
                <DatePicker
                  required
                  endDate={values.to}
                  error={errors.from}
                  maxDate={values.to || new Date()}
                  value={values.from}
                  onChange={(date) => {
                    const fromDate = DateTime.fromJSDate(date, {
                      zone: "local",
                    }).toFormat(dbDateFormat)
                    setFieldValue("from", fromDate)
                    setSearchParams((prev) => {
                      const newParams = new URLSearchParams(prev)
                      newParams.set("from", fromDate)
                      return newParams
                    })
                    if (!values.to || date > values.to) {
                      setFieldValue("to", null)
                    }
                  }}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs mb-1 text-white/80">To Date</label>
                <DatePicker
                  required
                  error={errors.to}
                  maxDate={new Date()}
                  minDate={values.from}
                  value={values.to}
                  onChange={(date) => {
                    const toDate = DateTime.fromJSDate(date, {
                      zone: "local",
                    }).toFormat(dbDateFormat)
                    setFieldValue("to", toDate)
                    setSearchParams((prev) => {
                      const newParams = new URLSearchParams(prev)
                      newParams.set("to", toDate)
                      return newParams
                    })
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Statistics Cards */}
      <DashboardStatisticsCards filters={values} />

      <div className="flex flex-wrap gap-6 w-full">
        <div className="w-full">
          <PatientOrdersStatistics
            filters={values}
            sorting={orderStatsSorting}
          />
        </div>
        <div className="w-full">
          <OrderResultDetails
            filters={values}
            sorting={resultStatsSorting}
            onSortingChange={setResultStatsSorting}
          />
        </div>
        <div />
      </div>
    </div>
  )
}

export default OrderComponents
